import { useState } from 'react';
import type { TaskDto } from '@/api/generated';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import styles from './TaskCalendar.module.css';

const WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const MONTHS = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

function startOffset(year: number, month: number): number {
  const jsDay = new Date(year, month, 1).getDay(); // zo=0
  return (jsDay + 6) % 7; // maandag=0
}

export function TaskCalendar({
  tasks,
  onSelect,
}: {
  tasks: TaskDto[];
  onSelect: (t: TaskDto) => void;
}) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = startOffset(year, month);

  const byDay = new Map<number, TaskDto[]>();
  for (const t of tasks) {
    // Parse the Y/M/D parts into a local Date so bucketing matches the
    // calendar cells regardless of timezone. `new Date('2026-06-03')` parses
    // as UTC midnight, which getMonth()/getDate() then read back in local
    // time — shifting the task a day on negative-UTC offsets.
    const [yy, mm, dd] = t.due.slice(0, 10).split('-').map(Number);
    const d = new Date(yy, mm - 1, dd);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      byDay.set(day, [...(byDay.get(day) ?? []), t]);
    }
  }

  const prev = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };
  const next = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  return (
    <div className={styles.calendar}>
      <div className={`row ${styles.calHead}`}>
        <Btn variant="ghost" onClick={prev} aria-label="Vorige maand">
          <Icon name="chevL" size={14} />
        </Btn>
        <span className={styles.calTitle}>
          {MONTHS[month]} {year}
        </span>
        <Btn variant="ghost" onClick={next} aria-label="Volgende maand">
          <Icon name="chevR" size={14} />
        </Btn>
      </div>
      <div className={styles.grid}>
        {WEEKDAYS.map((d) => (
          <div key={d} className={styles.weekday}>
            {d}
          </div>
        ))}
        {Array.from({ length: offset }).map((_, i) => (
          <div key={`pad-${i}`} className={styles.pad} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayTasks = byDay.get(day) ?? [];
          return (
            <div key={day} className={styles.cell}>
              <span className={styles.dayNum}>{day}</span>
              {dayTasks.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`${styles.event} ${t.urgent ? styles.eventUrgent : ''}`}
                  onClick={() => onSelect(t)}
                  title={t.title}
                >
                  {t.title}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
